import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function planForCount(count) {
  if (count <= 5)  return 'starter';
  if (count <= 20) return 'landlord';
  return 'portfolio';
}

function AuthShell({ children, title, sub }) {
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <h1 style={{ fontSize:36, fontWeight:300 }}>Landlord<em>Comply</em></h1>
          <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:6 }}>UK Compliance Hub</p>
        </div>
        <div style={{ background:'var(--bg-card)', border:'1.5px solid var(--border)', borderRadius:14, padding:36, boxShadow:'var(--shadow)' }}>
          <h2 style={{ fontSize:22, marginBottom:6 }}>{title}</h2>
          <p style={{ fontSize:14, color:'var(--ink-3)', marginBottom:28 }}>{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Signup() {
  const { login }    = useAuth();
  const navigate     = useNavigate();

  function getHeaders(token) {
    const accessCode = localStorage.getItem('lc_access_code');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(accessCode ? { 'X-Access-Code': accessCode } : {}),
    };
  }

  const [step, setStep]         = useState('details'); // details | otp
  const [fullName, setFullName] = useState('');
  const [email, setEmail]       = useState('');
  const [propCount, setPropCount] = useState('1');
  const [code, setCode]         = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const plan = planForCount(parseInt(propCount) || 1);
  const PLAN_NAMES = { starter:'Starter (£9/mo)', landlord:'Landlord (£19/mo)', portfolio:'Portfolio (£49/mo)' };

  async function handleSubmitDetails(e) {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/request-signup-otp', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  }

  async function handleVerifyOTP(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          email,
          code,
          full_name:      fullName,
          property_count: parseInt(propCount) || 1,
          plan,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');

      // Go to Stripe BEFORE calling login() to avoid React re-renders
      // that might interfere with the redirect
      const checkoutRes = await fetch('/api/billing/create-trial-checkout', {
        method: 'POST',
        headers: getHeaders(data.token),
        body: JSON.stringify({ plan }),
      });
      const checkoutData = await checkoutRes.json();

      if (!checkoutRes.ok || !checkoutData.url) {
        throw new Error(checkoutData.error || 'Could not start billing setup — please try again');
      }

      // Store auth so they're logged in when Stripe redirects back
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.landlord));

      // Redirect to Stripe — no login() call to avoid React re-render
      window.location.href = checkoutData.url;
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  }

  if (step === 'details') {
    return (
      <AuthShell title="Create your account" sub="Start your 14-day free trial — no charge until day 15">
        <form onSubmit={handleSubmitDetails}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>Full name</label>
            <input
              type="text" value={fullName} onChange={e => setFullName(e.target.value)}
              placeholder="Jane Smith" required autoFocus
              style={{ width:'100%', padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:15, background:'var(--bg)', color:'var(--ink)', boxSizing:'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>Email address</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" required
              style={{ width:'100%', padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:15, background:'var(--bg)', color:'var(--ink)', boxSizing:'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>How many properties do you manage?</label>
            <input
              type="number" min="1" max="999" value={propCount}
              onChange={e => setPropCount(e.target.value)}
              required
              style={{ width:'100%', padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:15, background:'var(--bg)', color:'var(--ink)', boxSizing:'border-box' }}
            />
            <p style={{ fontSize:12, color:'var(--ink-3)', marginTop:6 }}>
              Recommended plan: <strong>{PLAN_NAMES[plan]}</strong>
            </p>
          </div>

          <label style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:13, color:'var(--ink-3)', marginBottom:16, cursor:'pointer', lineHeight:1.6, width:'100%', boxSizing:'border-box' }}>
            <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}
              style={{ marginTop:3, flexShrink:0, width:16, height:16, cursor:'pointer' }} />
            <span style={{ display:'block', wordBreak:'break-word' }}>
              I agree to the{' '}
              <a href="/terms" target="_blank" style={{ color:'var(--accent)', whiteSpace:'nowrap' }}>Terms & Conditions</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" style={{ color:'var(--accent)', whiteSpace:'nowrap' }}>Privacy Policy</a>
            </span>
          </label>

          {error && <p style={{ color:'var(--danger)', fontSize:13, marginBottom:16 }}>{error}</p>}

          <button type="submit" disabled={loading || !agreedToTerms}
            style={{ width:'100%', padding:'13px', background:'var(--ink)', color:'#fff', border:'none', borderRadius:8, fontSize:15, cursor:loading?'wait':'pointer', opacity:loading?0.7:1 }}>
            {loading ? 'Sending code…' : 'Continue →'}
          </button>

          <p style={{ textAlign:'center', marginTop:20, fontSize:13, color:'var(--ink-3)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color:'var(--accent)' }}>Sign in</Link>
          </p>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Check your email" sub={`We sent a 6-digit code to ${email}`}>
      <form onSubmit={handleVerifyOTP}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>6-digit code</label>
          <input
            type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000" autoFocus required
            style={{ width:'100%', padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:28, letterSpacing:10, textAlign:'center', background:'var(--bg)', color:'var(--ink)', boxSizing:'border-box', fontFamily:'var(--font-mono)' }}
          />
        </div>

        {error && <p style={{ color:'var(--danger)', fontSize:13, marginBottom:16 }}>{error}</p>}

        <button type="submit" disabled={loading}
          style={{ width:'100%', padding:'13px', background:'var(--ink)', color:'#fff', border:'none', borderRadius:8, fontSize:15, cursor:loading?'wait':'pointer', opacity:loading?0.7:1 }}>
          {loading ? 'Verifying…' : 'Verify & start trial →'}
        </button>

        <button type="button" onClick={() => { setStep('details'); setCode(''); setError(''); }}
          style={{ width:'100%', textAlign:'center', marginTop:14, fontSize:13, color:'var(--ink-3)', background:'none', border:'none', cursor:'pointer' }}>
          ← Go back
        </button>

        <p style={{ textAlign:'center', marginTop:8, fontSize:12, color:'var(--ink-3)' }}>
          Didn't get it?{' '}
          <button type="button" onClick={handleSubmitDetails}
            style={{ background:'none', border:'none', color:'var(--accent)', cursor:'pointer', fontSize:12, fontWeight:500 }}>
            Resend code
          </button>
        </p>
      </form>
    </AuthShell>
  );
}